import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import style from "./ChooseUsernamePassword.module.css";
import Footer from "../components/Footer";
import TwoColLayout from "../../Templates/TwoColLayout";
import MainTemplate from "../../Templates/MainTemplate";
import StylePack from "../../../util/StylePack";
import TitleText, { TitleTextSize } from "../../../components/TitleText";
import Paragraph, { ParagraphSize } from "../../../components/Paragraph";
import InputBox, {
  InputBoxRoundedCornors,
  InputBoxState,
} from "../../../components/InputBox";
import { ButtonType, JokerButton } from "material-ui-jokerpro";
import AssetsPack from "../../../util/AssetsPack";
import { isValidPassword, isValidUsername } from "../../../util/Validator";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../store/hooks";
import {
  checkUsernameExist,
  onBoardingActions,
} from "../../../store/on-boarding-slice";
import { showConsoleError } from "../../../util/ConsoleMessage";

const ChooseUsernamePassword = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [username, setUsername] = useState("");
  const [usernameState, setUsernameState] = useState<InputBoxState>(
    InputBoxState.default
  );

  const [errorValue, setErrorValue] = useState<string | undefined>();
  const [errorValuePassword, setErrorValuePassword] = useState<
    string | undefined
  >();
  const [errorValueConfirmPassword, setErrorValueConfirmPassword] = useState<
    string | undefined
  >();

  const [password, setPassword] = useState("");
  const [passwordState, setPasswordState] = useState<InputBoxState>(
    InputBoxState.default
  );

  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmPasswordState, setConfirmPasswordState] =
    useState<InputBoxState>(InputBoxState.default);

  const handleContinue = async (e: { preventDefault: () => void }) => {
    e.preventDefault();

    try {
      await dispatch(checkUsernameExist(username));
      dispatch(onBoardingActions.setPassword(password));
      navigate("/choose_username");
    } catch (error: any) {
      showConsoleError(error);
      setUsernameState(InputBoxState.error);
      setErrorValue(error.data.error);
    }
  };

  const [continueDisabled, setContinueDisabled] = useState(true);

  const handleCancel = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    navigate(-1);
  };

  const getInputState = (value: any) => {
    if (value.length === 0) {
      return InputBoxState.default;
    }

    if (isValidPassword(value)) {
      return InputBoxState.success;
    } else {
      return InputBoxState.error;
    }
  };

  useEffect(() => {
    const validatePasswords = () => {
      const passwordStateValue = getInputState(password);
      const newErrorValue =
        passwordStateValue === InputBoxState.error
          ? t("error.invalid_login.invalid_password")
          : undefined;
      setErrorValuePassword(newErrorValue);
      setPasswordState(passwordStateValue);

      const confirmPasswordStateValue = getInputState(confirmPassword);
      let newConfirmErrorValue: string | undefined = undefined;

      if (confirmPasswordStateValue === InputBoxState.success) {
        if (password === confirmPassword) {
          setConfirmPasswordState(InputBoxState.success);
        } else {
          newConfirmErrorValue = t("error.invalid_login.password_not_match");
          setConfirmPasswordState(InputBoxState.error);
        }
      } else {
        if (confirmPasswordStateValue === InputBoxState.error) {
          newConfirmErrorValue = t("error.invalid_login.assword_not_match");
        }
        setConfirmPasswordState(confirmPasswordStateValue);
      }

      setErrorValueConfirmPassword(newConfirmErrorValue);
    };

    validatePasswords();

    checkContinue();
  }, [username, password, confirmPassword]);

  const checkContinue = () => {
    setContinueDisabled(
      !(
        isValidUsername(username) &&
        password === confirmPassword &&
        isValidPassword(confirmPassword)
      )
    );
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUsername(value);
    if (value.length === 0) {
      setUsernameState(InputBoxState.default);
      setErrorValue(undefined);
    } else if (isValidUsername(value)) {
      setUsernameState(InputBoxState.success);
      setErrorValue(undefined);
    } else {
      setUsernameState(InputBoxState.error);
      setErrorValue(t("error.invalid_login.invalid_user_name"));
    }
  };

  const leftContent = (
    <div>
      <form className={style["form-container"]}>
        <TitleText size={TitleTextSize.heading3}>
          {t("signup_page.choose_your_username_title")}
        </TitleText>
        <Paragraph size={ParagraphSize.paragraph2}>
          {t("signup_page.choose_your_username_subtitle")}
        </Paragraph>

        <div className={style["input-container"]}>
          <InputBox
            state={usernameState}
            roundedCornors={InputBoxRoundedCornors.all}
            type="username"
            placeholder={t("signup_page.username_placeholder")}
            value={username}
            onChange={handleUsernameChange}
            errorMessage={errorValue}
          />

          <InputBox
            state={passwordState}
            roundedCornors={InputBoxRoundedCornors.all}
            type="password"
            placeholder={t("signup_page.password_placeholder")}
            value={password}
            onChange={(e: any) => setPassword(e.target.value)}
            errorMessage={errorValuePassword}
          />

          <InputBox
            state={confirmPasswordState}
            roundedCornors={InputBoxRoundedCornors.all}
            type="password"
            placeholder={t("signup_page.confirm_password_placeholder")}
            value={confirmPassword}
            onChange={(e: any) => setConfirmPassword(e.target.value)}
            errorMessage={errorValueConfirmPassword}
          />
        </div>

        <div className={style["button-group"]}>
          <div className="cancel-button-wrapper">
            <JokerButton
              text={t("global_component.cancel")}
              onClick={handleCancel}
              type={ButtonType.Text}
              icon={AssetsPack.icons.ICON_CLOSE.default}
              equal
            />
          </div>
          <JokerButton
            text={t("global_component.continue")}
            disabled={continueDisabled}
            type={ButtonType.CTA}
            icon={AssetsPack.icons.ICON_GO.default}
            equal
            animated
            onClick={handleContinue}
          />
        </div>
      </form>
    </div>
  );
  const body = <TwoColLayout leftContent={leftContent} />;

  return (
    <MainTemplate
      body={body}
      footer={<Footer />}
      hasOverlayFooter
      backgroundStyle={StylePack.BACKGROUND_LOGIN_STYLE}
    />
  );
};

export default ChooseUsernamePassword;
